
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Plus, Compass, HeartHandshake, ShieldCheck, MessagesSquare, TrendingUp, Sparkles, Users, Handshake, type LucideIcon } from 'lucide-react';
import {
  HeroBackdropLayers,
  ImageBackdropLayers,
  cn,
} from '@/components/help-center/latest-power-shared';
import { PortalCrystalSeparator } from '@/components/help-center/PortalCrystalSeparator';
import { StaffOnlineBadge } from '@/components/help-center/StaffOnlineBadge';
import { EquipeGlassTooltipContent } from '@/components/help-center/EquipeGlassTooltipContent';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type {
  SiteStaffJoinStepIcon,
  SiteStaffMember,
  SiteStaffRole,
  SiteStaffXatRank,
} from '@/lib/cms-types';
import type { StaffOnlinePresence } from '@/lib/staff-online-status';
import { resolveStaffXatRank } from '@/lib/equipe-xat-ranks';
import { getXatProfile } from '@/services/xat-proxy.service';
export const FLAG_IMG: Record<'BR' | 'PT', string> = {
  BR: '/flags/wbr.webp',
  PT: '/flags/wpt.webp',
};

export const COUNTRY_NAMES: Record<'BR' | 'PT', string> = {
  BR: 'Brasil',
  PT: 'Portugal',
};

/** Smilies xat usados no seletor do painel — mesmos códigos visíveis no chat. */
export const COUNTRY_SMILIE: Record<'BR' | 'PT', string> = {
  BR: '(wbr)',
  PT: '(wpt)',
};

export const rankPawnShellStyle: CSSProperties = {
  background: 'radial-gradient(circle at 50% 48%, #112268 0%, #0a1450 42%, #060c38 75%, #030719 100%)',
  boxShadow: '0 0 0 1.5px #0b1658, 0 0 0 2.5px #0f1e70',
};

export const RANK_ICONS: Record<SiteStaffRole, string> = {
  mainOwner: '/pawns/mainowner.png',
  owners: '/pawns/owner.png',
  moderadores: '/pawns/mod.png',
  membros: '/pawns/member.png',
};

/** Cor dominante de cada peão xat — usada nas caixas de descrição do cargo em /equipe. */
export const STAFF_ROLE_PAWN_COLORS: Record<SiteStaffRole, string> = {
  mainOwner: '#f7941d',
  owners: '#ffcc00',
  moderadores: '#c8cdd8',
  membros: '#4f5fd6',
};

export const STAFF_JOIN_STEP_ICONS: Record<SiteStaffJoinStepIcon, LucideIcon> = {
  Compass,
  HeartHandshake,
  ShieldCheck,
  MessagesSquare,
  TrendingUp,
  Sparkles,
  Users,
  Handshake,
};

export const STAFF_JOIN_STEP_ICON_OPTIONS: SiteStaffJoinStepIcon[] = [
  'Compass',
  'HeartHandshake',
  'ShieldCheck',
  'MessagesSquare',
  'TrendingUp',
  'Sparkles',
  'Users',
  'Handshake',
];

/** Tema unificado das caixas de descrição na página pública /equipe. */
const STAFF_ROLE_DESCRIPTION_PUBLIC_THEME = {
  text: 'text-blue-100/80',
  /** Light mode: card com degradê navy — texto claro. */
  textLight: 'text-blue-100/90',
  dot: 'bg-sky-400/80',
} as const;

/** @deprecated Temas por cargo — a página pública usa o card estilo introdução. */
const STAFF_ROLE_DESCRIPTION_THEME: Record<
  SiteStaffRole,
  { text: string; textLight: string; dot: string }
> = {
  mainOwner: STAFF_ROLE_DESCRIPTION_PUBLIC_THEME,
  owners: STAFF_ROLE_DESCRIPTION_PUBLIC_THEME,
  moderadores: STAFF_ROLE_DESCRIPTION_PUBLIC_THEME,
  membros: STAFF_ROLE_DESCRIPTION_PUBLIC_THEME,
};

const STAFF_ROLE_DESCRIPTION_DEFAULT_TEXT: Record<SiteStaffRole, string> = {
  mainOwner:
    'Responsáveis pela gestão geral do chat e do site, incluindo decisões administrativas, estratégicas e técnicas.\n\nTambém atuam como ponte principal entre os usuários e a equipe, garantindo que tudo funcione de forma alinhada.',
  owners:
    'Cuidam da coordenação geral do chat e da equipe.\n\nTrabalham junto aos moderadores e membros, ajudando a manter a organização, o bom funcionamento e a comunicação com os usuários.',
  moderadores:
    'Responsáveis por manter a ordem no chat e dar suporte aos usuários.\n\nAtuam diretamente na moderação, garantindo que as regras sejam seguidas e que o ambiente continue saudável para todos.',
  membros:
    'Ajudam no suporte geral dentro do chat, tirando dúvidas e orientando usuários.\n\nUsuários que se destacam podem receber oportunidades para auxiliar na moderação temporariamente e evoluir dentro da equipe.',
};

function parseStaffRoleDescriptionParts(
  text: string | null | undefined,
  role: SiteStaffRole
): string[] {
  const t = String(text ?? '').trim();
  const source = t || STAFF_ROLE_DESCRIPTION_DEFAULT_TEXT[role];
  return source
    .split(/\n\s*\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const MAX_XAT_TOON_AVATAR_INDEX = 20_000;
const XAT_DEFAULT_TOON_COUNT = 1760;

export function xatWebGearAvatarUrl(avatarField: string | null | undefined): string | null {
  const t = String(avatarField ?? '').trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0 || n > MAX_XAT_TOON_AVATAR_INDEX) return null;
  return `https://www.xat.com/web_gear/chat/av/${t}.png`;
}

export function avatarFieldDirectImageUrl(avatarField: string | null | undefined): string | null {
  const t = String(avatarField ?? '').trim();
  if (!t || /^\d+$/.test(t)) return null;
  const lower = t.toLowerCase();
  if (lower.startsWith('https://') || lower.startsWith('http://')) return t;
  if (lower.startsWith('//')) return `https:${t}`;
  return null;
}

export function memberGearCandidateUrl(member: SiteStaffMember): string | null {
  // Avatar numérico = toon do xat; URL fica para avatarFieldDirectImageUrl.
  const explicit = xatWebGearAvatarUrl(member.avatar);
  if (explicit) return explicit;
  // Avatar já é URL → não derivar toon (senão sobrescreve a imagem).
  if (avatarFieldDirectImageUrl(member.avatar)) return null;
  // Sem avatar definido: toon automático a partir do xat ID.
  const id = String(member.xatId ?? '').trim();
  if (!/^\d+$/.test(id)) return null;
  const idx = Number(id) % XAT_DEFAULT_TOON_COUNT;
  return `https://www.xat.com/web_gear/chat/av/${idx}.png`;
}

export const STAFF_ROLE_ORDER: SiteStaffRole[] = ['mainOwner', 'owners', 'moderadores', 'membros'];

/** Colunas por linha na grade de cards no maior breakpoint (usado p/ slots "+" no painel). */
const STAFF_GRID_COLUMNS = 4;

/** Largura máxima da grade de cards no painel — bloco centralizado na página. */
const STAFF_PAINEL_CARDS_MAX_WIDTH = 'max-w-[1280px]';

export const STAFF_SECTION_LABEL: Record<SiteStaffRole, string> = {
  mainOwner: 'Main Owners',
  owners: 'Owners',
  moderadores: 'Moderadores',
  membros: 'Membros',
};

export const RANK_TOOLTIP_SINGULAR: Record<string, string> = {
  'Main Owners': 'Main Owner',
  Owners: 'Owner',
  Moderadores: 'Moderador',
  Membros: 'Membro',
};

const roleDescItemTextClass =
  'min-w-0 flex-1 hyphens-none text-pretty font-inter text-[15px] font-normal leading-relaxed';

export const avatarGradients = [
  'from-blue-500 to-blue-600',
  'from-violet-500 to-purple-600',
  'from-emerald-500 to-green-600',
  'from-rose-500 to-pink-600',
  'from-amber-500 to-orange-500',
  'from-sky-500 to-cyan-600',
];

export function getGradient(name: string) {
  const code = name.length > 0 ? name.charCodeAt(0) : 65;
  return avatarGradients[Math.abs(code) % avatarGradients.length];
}

export function StaffXatRankBadge({ rank }: { rank: SiteStaffXatRank }) {
  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex max-w-full cursor-default items-center justify-center whitespace-nowrap rounded-full border px-3 py-1 font-inter text-xs font-semibold leading-tight outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50"
            style={{
              color: rank.color,
              borderColor: `color-mix(in srgb, ${rank.color} 42%, transparent)`,
              backgroundColor: `color-mix(in srgb, ${rank.color} 16%, transparent)`,
              boxShadow: `0 0 14px -4px color-mix(in srgb, ${rank.color} 55%, transparent)`,
            }}
          >
            {rank.label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="font-poppins text-sm font-medium">
          {rank.label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Altura reservada abaixo do nome — com ou sem rank xat, para alinhar as linhas País / Username / xat ID. */
export const staffXatRankBadgeSlotClass =
  'flex h-7 w-full shrink-0 items-center justify-center';

export const memberInfoRowClass = cn(
  'flex h-12 w-full shrink-0 items-center justify-between gap-3 rounded-2xl px-3.5',
  'border border-white/[0.08] bg-gradient-to-br from-white/[0.07] to-white/[0.02]',
  'shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-sm',
  'transition-colors duration-200 hover:border-sky-400/25 hover:from-white/[0.09]'
);

export function groupStaffByRole(members: SiteStaffMember[]): Record<SiteStaffRole, SiteStaffMember[]> {
  const out: Record<SiteStaffRole, SiteStaffMember[]> = {
    mainOwner: [],
    owners: [],
    moderadores: [],
    membros: [],
  };
  for (const m of members) {
    const bucket = out[m.role];
    if (bucket) bucket.push(m);
    else out.membros.push(m);
  }
  for (const role of STAFF_ROLE_ORDER) {
    out[role].sort((a, b) =>
      String(a.name ?? '').localeCompare(String(b.name ?? ''), 'pt', {
        sensitivity: 'base',
        numeric: true,
      })
    );
  }
  return out;
}

type EquipeMemberCardProps = {
  member: SiteStaffMember;
  rankIconSrc: string;
  rankLabel: string;
  adminSlot?: ReactNode;
  /** Só no painel: contorno suave à volta do cartão. */
  cardFrame?: 'default' | 'painel';
  /** Presença online (proxy interno). */
  onlinePresence?: StaffOnlinePresence;
};

export function EquipeMemberCard({
  member,
  rankIconSrc,
  rankLabel,
  adminSlot,
  cardFrame = 'default',
  onlinePresence,
}: EquipeMemberCardProps) {
  const usernameSafe = String(member.username ?? '').trim();
  const countrySafe: 'BR' | 'PT' = member.country === 'PT' ? 'PT' : 'BR';
  const displayName = String(member.name ?? '').trim();
  const xatRank = resolveStaffXatRank(member);
  const xatIdSafe = String(member.xatId ?? '').trim();
  const xatspaceHref = usernameSafe
    ? `https://xat.me/${encodeURIComponent(usernameSafe)}`
    : undefined;
  const flagSrc = FLAG_IMG[countrySafe];
  const gearFromAvatarNumber = xatWebGearAvatarUrl(member.avatar);
  const directCandidateUrl = avatarFieldDirectImageUrl(member.avatar);
  const gearFromXatId =
    !gearFromAvatarNumber && !directCandidateUrl ? memberGearCandidateUrl(member) : null;
  const [gearBroken, setGearBroken] = useState(false);
  const [directBroken, setDirectBroken] = useState(false);
  const [profileAvatarUrl, setProfileAvatarUrl] = useState<string | null>(null);

  const numberGearUrl = gearFromAvatarNumber && !gearBroken ? gearFromAvatarNumber : null;
  const xatIdGearUrl = gearFromXatId && !gearBroken ? gearFromXatId : null;
  const directAvatarUrl = directCandidateUrl && !directBroken ? directCandidateUrl : null;
  // Prioridade: número (toon) → URL → perfil xat → toon do xat ID.
  const displayAvatarUrl =
    numberGearUrl ?? directAvatarUrl ?? profileAvatarUrl ?? xatIdGearUrl;

  useEffect(() => {
    if (numberGearUrl || directAvatarUrl) return undefined;
    let cancelled = false;
    const id = String(member.xatId ?? '').trim();
    if (!id || !/^\d+$/.test(id)) return undefined;

    const load = async () => {
      try {
        const j = await getXatProfile({ id });
        if (!cancelled && j.avatarUrl) setProfileAvatarUrl(j.avatarUrl);
      } catch {
        /* mantém fallback com inicial / toon */
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [member.xatId, numberGearUrl, directAvatarUrl]);

  useEffect(() => {
    setGearBroken(false);
    setDirectBroken(false);
    setProfileAvatarUrl(null);
  }, [member.xatId, member.avatar]);

  return (
    <div className="group relative min-w-0 rounded-2xl p-px">
      <span
        className="pointer-events-none absolute inset-0 rounded-2xl transition-opacity duration-200 group-hover:opacity-0"
        style={{
          background: `linear-gradient(to bottom, color-mix(in srgb, ${STAFF_ROLE_PAWN_COLORS.membros} 40%, transparent), transparent)`,
        }}
        aria-hidden
      />
      <span
        className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-200 group-hover:opacity-100"
        style={{
          background: `linear-gradient(to bottom, color-mix(in srgb, ${STAFF_ROLE_PAWN_COLORS.membros} 85%, transparent), transparent)`,
        }}
        aria-hidden
      />
      <div
        className={cn(
          'relative z-[1] overflow-hidden rounded-[15px] bg-blue-950/30',
          cardFrame === 'painel' && 'ring-1 ring-inset ring-sky-400/10'
        )}
      >
        {adminSlot}
        <ImageBackdropLayers className="pointer-events-none absolute inset-0 overflow-hidden rounded-[15px]" />
        <div className="pointer-events-none absolute inset-0 rounded-[15px] bg-slate-950/72" aria-hidden />
        <div
          className="pointer-events-none absolute inset-0 rounded-[15px] bg-gradient-to-b from-[#030023]/45 via-transparent to-[#030023]/80"
          aria-hidden
        />

        <div className="relative z-10 flex flex-col items-center gap-4 px-4 pb-5 pt-5 md:px-6 md:pb-6">
        <div className="relative shrink-0">
          <div
            className={cn(
              'flex h-[4.75rem] w-[4.75rem] items-center justify-center overflow-hidden rounded-full shadow-lg ring-2 ring-white/10',
              displayAvatarUrl
                ? 'bg-transparent'
                : cn('bg-gradient-to-br text-xl font-bold text-white', getGradient(displayName || '?'))
            )}
          >
            {displayAvatarUrl ? (
              <img
                src={displayAvatarUrl}
                alt=""
                className="h-full w-full object-contain object-center"
                loading="lazy"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  const failedSrc = e.currentTarget.getAttribute('src');
                  if (
                    failedSrc &&
                    ((gearFromAvatarNumber && failedSrc === gearFromAvatarNumber) ||
                      (gearFromXatId && failedSrc === gearFromXatId))
                  ) {
                    setGearBroken(true);
                  }
                  if (failedSrc && directCandidateUrl && failedSrc === directCandidateUrl) {
                    setDirectBroken(true);
                  }
                }}
              />
            ) : /^\d+$/.test(String(member.avatar ?? '').trim()) ? (
              <span className="text-xl font-bold text-white">
                {(displayName || '?').charAt(0) || '?'}
              </span>
            ) : (
              member.avatar ?? ''
            )}
          </div>
          {onlinePresence != null ? <StaffOnlineBadge presence={onlinePresence} /> : null}
        </div>

        <div className="flex min-w-0 max-w-full flex-col items-center gap-2 px-1">
          <h3 className="flex min-w-0 max-w-full items-center justify-center gap-0.5 font-poppins text-lg font-bold text-white">
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="inline-flex shrink-0 cursor-default items-center justify-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#030023]"
                  aria-label={rankLabel}
                >
                  <img
                    src={rankIconSrc}
                    alt=""
                    className="pointer-events-none block h-5 w-5 object-contain object-center"
                    style={{ transform: 'translate(22%, calc(12% - 1px))' }}
                    width={20}
                    height={20}
                    draggable={false}
                    loading="lazy"
                  />
                </span>
              </TooltipTrigger>
              <EquipeGlassTooltipContent>
                <p className="font-poppins text-[12px] font-semibold tracking-wide text-white">
                  {rankLabel}
                </p>
              </EquipeGlassTooltipContent>
            </Tooltip>
            <span className="-ml-0.5 min-w-0 truncate text-center" title={displayName || undefined}>
              {displayName || '—'}
            </span>
          </h3>
          <div className={staffXatRankBadgeSlotClass} aria-hidden={!xatRank}>
            {xatRank ? <StaffXatRankBadge rank={xatRank} /> : null}
          </div>
        </div>

        <div className="flex w-full flex-col gap-2">
          <div className={memberInfoRowClass}>
            <span className="shrink-0 font-poppins text-[11px] font-semibold leading-tight text-white sm:text-xs">
              País
            </span>
            <div className="flex h-full min-h-0 shrink-0 items-center justify-center">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-default rounded-md outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#030023]">
                    <img
                      src={flagSrc}
                      alt={COUNTRY_NAMES[countrySafe]}
                      loading="lazy"
                      className="h-9 w-auto max-w-none object-contain drop-shadow-[0_2px_14px_rgba(0,0,0,0.45)] sm:h-10"
                      draggable={false}
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="font-poppins text-popover-foreground">
                  {COUNTRY_NAMES[countrySafe]}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          <div className={memberInfoRowClass}>
            <span className="shrink-0 font-poppins text-[11px] font-semibold leading-tight text-white sm:text-xs">
              Username
            </span>
            <span className="min-w-0 truncate text-right font-inter text-xs font-medium text-blue-100/90 sm:text-sm">
              {usernameSafe || '—'}
            </span>
          </div>

          <div className={memberInfoRowClass}>
            <span className="shrink-0 font-poppins text-[11px] font-semibold leading-tight text-white sm:text-xs">
              xat ID
            </span>
            <span className="font-inter text-xs font-medium tabular-nums text-blue-100/90 sm:text-sm">
              {xatIdSafe || '—'}
            </span>
          </div>

          <PortalCrystalSeparator className="my-1" />

          <a
            href={xatspaceHref ?? '#'}
            target={xatspaceHref ? '_blank' : undefined}
            rel={xatspaceHref ? 'noopener noreferrer' : undefined}
            onClick={!xatspaceHref ? (e) => e.preventDefault() : undefined}
            draggable={false}
            className={cn(
              'inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-2xl px-3 font-poppins text-sm font-normal text-white',
              'border border-white/[0.12] bg-gradient-to-br from-white/[0.08] to-white/[0.02]',
              'shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-sm',
              'transition-all duration-200',
              xatspaceHref ? 'hover:bg-white/[0.12]' : 'cursor-not-allowed opacity-45'
            )}
            aria-disabled={!xatspaceHref}
            title={!xatspaceHref ? 'Defina o username para abrir o xatspace' : undefined}
          >
            <span>ver</span>
            <span className="font-bold">xatspace</span>
          </a>
        </div>
      </div>
      </div>
    </div>
  );
}

/** Parágrafos a partir de texto CMS (blocos separados por linha em branco). */
/** Tom azul-escuro unificado para as caixas no painel (harmoniza com o resto do painel). */
const STAFF_ROLE_DESCRIPTION_PAINEL_THEME = {
  box: 'border-white/10 bg-blue-950/40',
  text: 'text-blue-100/75',
  textLight: 'text-blue-100/75',
  dot: 'bg-sky-400/70',
} as const;

function StaffRoleDescriptionList({
  role,
  text,
  isPainel = false,
  isLight = false,
}: {
  role: SiteStaffRole;
  text: string | null | undefined;
  isPainel?: boolean;
  isLight?: boolean;
}) {
  const parts = parseStaffRoleDescriptionParts(text, role);
  const theme = isPainel
    ? STAFF_ROLE_DESCRIPTION_PAINEL_THEME
    : STAFF_ROLE_DESCRIPTION_THEME[role];
  const pawnColor = STAFF_ROLE_PAWN_COLORS[role];

  if (!isPainel) {
    return (
      <div className="relative mt-3 w-full">
        {parts.length > 1 ? (
          <div
            className="pointer-events-none absolute left-[3px] w-px"
            style={{
              top: 'calc(0.55em + 3px)',
              bottom: 'calc(0.55em + 3px)',
              background: `linear-gradient(to bottom, color-mix(in srgb, ${pawnColor} 65%, transparent), color-mix(in srgb, ${pawnColor} 18%, transparent), transparent)`,
            }}
            aria-hidden
          />
        ) : null}
        <ol className="relative m-0 list-none space-y-0 p-0">
          {parts.map((p, i) => {
            const isLast = i === parts.length - 1;
            return (
              <li
                key={i}
                className={cn('relative flex gap-2.5', !isLast && 'pb-2.5 sm:pb-3')}
              >
                <span
                  className="mt-[0.55em] h-1.5 w-1.5 shrink-0 rounded-full"
                  style={{
                    backgroundColor: pawnColor,
                    boxShadow: `0 0 6px 1px color-mix(in srgb, ${pawnColor} 45%, transparent)`,
                  }}
                  aria-hidden
                />
                <p
                  className={cn(
                    'min-w-0 flex-1 font-inter text-sm leading-relaxed sm:text-[15px]',
                    isLight ? theme.textLight : theme.text
                  )}
                >
                  {p}
                </p>
              </li>
            );
          })}
        </ol>
      </div>
    );
  }

  return (
    <ul className="m-0 list-none space-y-2.5 p-0">
      {parts.map((p, i) => (
        <li key={i} className="flex gap-2.5">
          <span className={cn('mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full', theme.dot)} aria-hidden />
          <p className={cn(roleDescItemTextClass, theme.text)}>{p}</p>
        </li>
      ))}
    </ul>
  );
}

function StaffRoleDescriptionInfoBox({
  role,
  roleLabel,
  rankIconSrc,
  children,
  isPainel = false,
  isLight = false,
}: {
  role: SiteStaffRole;
  roleLabel: string;
  rankIconSrc: string;
  children: ReactNode;
  isPainel?: boolean;
  isLight?: boolean;
}) {
  const pawnColor = STAFF_ROLE_PAWN_COLORS[role];

  if (isPainel) {
    return (
      <div
        className={cn(
          'mb-8 w-full rounded-2xl border px-4 py-3.5 md:px-5 md:py-4',
          STAFF_ROLE_DESCRIPTION_PAINEL_THEME.box
        )}
      >
        {children}
      </div>
    );
  }

  return (
    <div
        className={cn(
          'relative mb-8 overflow-hidden rounded-3xl border backdrop-blur-sm px-5 py-7 sm:px-8 sm:py-8',
          isLight
            ? 'bg-gradient-to-br from-[#030023] via-[#071238] to-[#000b6a] shadow-[0_16px_48px_-24px_rgba(0,11,106,0.45)]'
            : 'bg-[#030023]/80'
        )}
      style={{
        borderColor: `color-mix(in srgb, ${pawnColor} ${isLight ? 42 : 38}%, transparent)`,
        boxShadow: isLight
          ? undefined
          : `0 0 48px -22px color-mix(in srgb, ${pawnColor} 45%, transparent)`,
      }}
    >
      <div
        className="pointer-events-none absolute -left-20 top-10 h-56 w-56 rounded-full blur-3xl"
        style={{
          backgroundColor: `color-mix(in srgb, ${pawnColor} ${isLight ? 10 : 16}%, transparent)`,
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-16 bottom-0 h-48 w-48 rounded-full blur-3xl"
        style={{
          backgroundColor: `color-mix(in srgb, ${pawnColor} ${isLight ? 6 : 10}%, transparent)`,
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent to-transparent"
        style={{
          backgroundImage: `linear-gradient(to right, transparent, color-mix(in srgb, ${pawnColor} 45%, transparent), transparent)`,
        }}
        aria-hidden
      />

      <div className="relative flex flex-col gap-5 sm:flex-row sm:items-start sm:gap-6">
        <span
          className="inline-flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-2xl border"
          style={{
            borderColor: `color-mix(in srgb, ${pawnColor} 45%, transparent)`,
            background: `linear-gradient(to bottom, color-mix(in srgb, ${pawnColor} 18%, transparent), transparent)`,
            boxShadow: `0 0 40px -8px color-mix(in srgb, ${pawnColor} 55%, transparent)`,
          }}
        >
          <img
            src={rankIconSrc}
            alt=""
            className="pointer-events-none h-8 w-8 object-contain object-center"
            style={{ transform: 'translate(22%, 12%)' }}
            width={32}
            height={32}
            draggable={false}
          />
        </span>

        <div className="min-w-0 flex-1">
          <p
            className="font-inter text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{
              color: `color-mix(in srgb, ${pawnColor} 85%, white)`,
            }}
          >
            Sobre o cargo
          </p>
          <h2 className="mt-1.5 font-poppins text-xl font-bold tracking-tight text-white sm:text-2xl">
            {roleLabel}
          </h2>
          {children}
        </div>
      </div>
    </div>
  );
}

export const STAFF_MEMBER_DRAG_MIME = 'application/xat-help-staff-id';

type EquipeRoleSectionProps = {
  role: SiteStaffRole;
  members: SiteStaffMember[];
  renderCard?: (member: SiteStaffMember, rankIconSrc: string, rankLabel: string) => ReactNode;
  /** Site público: títulos escuros sobre fundo claro. Painel: títulos/descrições claros, alinhados à esquerda. */
  sectionTone?: 'public' | 'painel';
  /** Página pública: aparência clara do chrome (não altera os cards de membros). */
  isLight?: boolean;
  /** Texto da descrição do cargo (CMS). Parágrafos: linha em branco entre blocos. */
  roleDescriptionText?: string | null;
  /** Conteúdo à direita do cabeçalho do rank (ex.: botão Editar no painel). */
  rankHeaderExtra?: ReactNode;
  /** Painel: callback para abrir o modal de novo membro já no cargo desta seção. Ativa os slots "+". */
  onAddMember?: (role: SiteStaffRole) => void;
  /** Presença online por xat ID (proxy interno). */
  onlineById?: Record<string, { presence: StaffOnlinePresence }>;
  /** Painel: arrastar cartões entre ranks para alterar `role`. */
  dragDrop?: {
    draggingMemberId: string | null;
    dropTargetRole: SiteStaffRole | null;
    onMemberDragStart: (memberId: string) => void;
    onDropZoneDragOver: (targetRole: SiteStaffRole) => void;
    onAssignMemberRole: (memberId: string, targetRole: SiteStaffRole) => void;
    onDragFinalize: () => void;
  };
};

export function EquipeRoleSection({
  role,
  members,
  renderCard,
  sectionTone = 'public',
  isLight = false,
  roleDescriptionText,
  rankHeaderExtra,
  onAddMember,
  onlineById,
  dragDrop,
}: EquipeRoleSectionProps) {
  const label = STAFF_SECTION_LABEL[role];
  const rankIconSrc = RANK_ICONS[role];
  const rankLabel = RANK_TOOLTIP_SINGULAR[label] ?? label;
  const isPainel = sectionTone === 'painel';
  const dnd = dragDrop;
  const isDndTarget =
    Boolean(dnd && dnd.draggingMemberId && dnd.dropTargetRole === role);

  // Painel: preenche a última linha (grade de 4) com slots "+"; se estiver
  // completa, mostra uma nova linha inteira de slots convidando a adicionar.
  const showAddSlots = isPainel && typeof onAddMember === 'function';
  const remainderInRow = members.length % STAFF_GRID_COLUMNS;
  const addSlotCount = showAddSlots
    ? remainderInRow === 0
      ? STAFF_GRID_COLUMNS
      : STAFF_GRID_COLUMNS - remainderInRow
    : 0;

  return (
    <div className={cn(isPainel && 'w-full text-left')}>
      {isPainel ? (
        <div
          className={cn(
            'mb-4 flex w-full flex-wrap items-center gap-3',
            rankHeaderExtra ? 'justify-between gap-x-4' : 'justify-start'
          )}
        >
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <div
              className="relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl"
              style={rankPawnShellStyle}
            >
              <img
                src={rankIconSrc}
                alt=""
                className="pointer-events-none h-8 w-8 object-contain object-center"
                style={{ transform: 'translate(22%, 12%)' }}
                width={32}
                height={32}
                draggable={false}
              />
            </div>
            <h2 className="font-poppins text-2xl font-bold tracking-tight text-white">{label}</h2>
            <span
              className="ml-1 inline-flex items-center rounded-full border-[1.5px] border-[#1a3099] px-2.5 py-0.5 font-poppins text-xs font-bold tabular-nums"
              style={{
                background: 'linear-gradient(135deg, #0e1f7a 0%, #091460 100%)',
                color: '#8aabf8',
              }}
            >
              {members.length}
            </span>
          </div>
          {rankHeaderExtra ? (
            <div className="ml-auto flex shrink-0 items-center">{rankHeaderExtra}</div>
          ) : null}
        </div>
      ) : null}

      <StaffRoleDescriptionInfoBox
        role={role}
        roleLabel={label}
        rankIconSrc={rankIconSrc}
        isPainel={false}
        isLight={isLight}
      >
        <StaffRoleDescriptionList
          role={role}
          text={roleDescriptionText}
          isPainel={false}
          isLight={isLight}
        />
      </StaffRoleDescriptionInfoBox>

      <div
        className={cn(
          'grid gap-4 lg:gap-6',
          isPainel
            ? cn(
                'grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
                'mx-auto w-full auto-rows-auto items-stretch',
                STAFF_PAINEL_CARDS_MAX_WIDTH
              )
            : 'grid-cols-1 auto-rows-fr sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
          isDndTarget && 'rounded-2xl ring-2 ring-inset ring-sky-400/45 transition-[box-shadow] duration-150'
        )}
        onDragOver={
          dnd
            ? (e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                dnd.onDropZoneDragOver(role);
              }
            : undefined
        }
        onDrop={
          dnd
            ? (e) => {
                e.preventDefault();
                const id = e.dataTransfer.getData(STAFF_MEMBER_DRAG_MIME);
                if (id) dnd.onAssignMemberRole(id, role);
                dnd.onDragFinalize();
              }
            : undefined
        }
      >
        {members.map((member) =>
          renderCard ? (
            <div
              key={member.id}
              draggable={Boolean(dnd)}
              onDragStart={
                dnd
                  ? (e) => {
                      e.dataTransfer.setData(STAFF_MEMBER_DRAG_MIME, member.id);
                      e.dataTransfer.effectAllowed = 'move';
                      dnd.onMemberDragStart(member.id);
                    }
                  : undefined
              }
              onDragEnd={dnd ? () => dnd.onDragFinalize() : undefined}
              className={cn(
                dnd && 'cursor-grab active:cursor-grabbing',
                dnd && dnd.draggingMemberId === member.id && 'opacity-55'
              )}
            >
              {renderCard(member, rankIconSrc, rankLabel)}
            </div>
          ) : (
            <EquipeMemberCard
              key={member.id}
              member={member}
              rankIconSrc={rankIconSrc}
              rankLabel={rankLabel}
              onlinePresence={
                onlineById?.[String(member.xatId ?? '').trim().toLowerCase()]?.presence
              }
            />
          )
        )}
        {showAddSlots
          ? Array.from({ length: addSlotCount }).map((_, i) => (
              <StaffAddMemberSlot
                key={`add-${role}-${i}`}
                onClick={() => onAddMember?.(role)}
                label={rankLabel}
              />
            ))
          : null}
      </div>
    </div>
  );
}

function StaffAddMemberSlot({
  onClick,
  label,
}: {
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Adicionar ${label}`}
      className={cn(
        'group flex h-full min-h-[240px] w-full flex-col items-center justify-center gap-3 self-stretch rounded-2xl',
        'border border-dashed border-white/15 bg-white/[0.02] p-6 text-center',
        'transition-all duration-200 hover:border-sky-400/50 hover:bg-sky-400/[0.06]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50'
      )}
    >
      <span
        className={cn(
          'flex h-12 w-12 items-center justify-center rounded-full',
          'border border-white/15 bg-white/[0.04] text-blue-100/70',
          'transition-all duration-200 group-hover:scale-105 group-hover:border-sky-400/50 group-hover:bg-sky-400/15 group-hover:text-sky-200'
        )}
      >
        <Plus className="h-6 w-6" />
      </span>
      <span className="font-inter text-xs font-medium text-blue-100/50 transition-colors group-hover:text-sky-200/90">
        Adicionar {label}
      </span>
    </button>
  );
}

export function EquipePageHero({
  title,
  subtitle,
  className,
  appearanceToggle,
  isLight = false,
}: {
  title: string;
  subtitle: string;
  className?: string;
  appearanceToggle?: ReactNode;
  isLight?: boolean;
}) {
  return (
    <section
      className={cn(
        'relative overflow-hidden bg-[#030023]',
        isLight && 'border-b border-blue-900/50',
        className
      )}
    >
      <HeroBackdropLayers className="absolute inset-0" />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#030023]"
        aria-hidden
      />
      <div className="pointer-events-none absolute inset-0 opacity-40" aria-hidden>
        <span className="absolute left-[12%] top-[28%] h-1 w-1 rounded-full bg-white/80 shadow-[0_0_6px_rgba(255,255,255,0.8)]" />
        <span className="absolute left-[78%] top-[34%] h-0.5 w-0.5 rounded-full bg-sky-100/70" />
        <span className="absolute left-[22%] top-[62%] h-0.5 w-0.5 rounded-full bg-white/60" />
        <span className="absolute left-[88%] top-[58%] h-1 w-1 rounded-full bg-white/50 shadow-[0_0_8px_rgba(125,211,252,0.5)]" />
        <span className="absolute left-[55%] top-[42%] h-0.5 w-0.5 rounded-full bg-white/50" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-6 pb-14 pt-28 sm:pb-16 sm:pt-32 md:pt-36">
        {/* Reserva a mesma altura da linha badge+toggle de Regras/Conduta */}
        <div className="mb-4 h-8 sm:mb-5" aria-hidden />
        <div className="flex items-center justify-between gap-4">
          <h1 className="min-w-0 font-poppins text-[2rem] font-bold leading-[1.15] tracking-tight text-white sm:text-4xl md:text-5xl">
            {title}
          </h1>
          {appearanceToggle}
        </div>
        <p className="mt-4 max-w-2xl font-poppins text-lg leading-relaxed text-[#B5BEDA] md:text-xl">
          {subtitle}
        </p>
      </div>
    </section>
  );
}

/** Pré-visualização editável do hero: mesmos estilos de título/subtítulo de /equipe, sem vídeo de fundo. */
export function EquipePageHeroEditor({
  title,
  subtitle,
  onTitleChange,
  onSubtitleChange,
  className,
}: {
  title: string;
  subtitle: string;
  onTitleChange: (value: string) => void;
  onSubtitleChange: (value: string) => void;
  className?: string;
}) {
  return (
    <section
      className={cn(
        'rounded-2xl border border-white/10 bg-[#030023] px-6 py-10 text-left shadow-lg shadow-black/20 md:px-8 md:py-12',
        className
      )}
    >
      <div className="w-full max-w-5xl">
        <label htmlFor="staff-hero-title-edit" className="sr-only">
          Título da página (hero)
        </label>
        <input
          id="staff-hero-title-edit"
          type="text"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Título do hero"
          autoComplete="off"
          className={cn(
            'mb-5 block w-full min-w-0 border-0 bg-transparent p-0 text-left font-poppins text-5xl font-bold text-white outline-none',
            'ring-0 ring-transparent placeholder:text-white/30 focus:ring-0',
            'md:text-6xl'
          )}
        />
        <label htmlFor="staff-hero-sub-edit" className="sr-only">
          Subtítulo da página (hero)
        </label>
        <textarea
          id="staff-hero-sub-edit"
          value={subtitle}
          onChange={(e) => onSubtitleChange(e.target.value)}
          placeholder="Subtítulo do hero (como na página pública)"
          rows={4}
          className={cn(
            'max-w-2xl w-full resize-y border-0 bg-transparent p-0 text-left font-poppins text-xl leading-relaxed text-blue-100/80 outline-none',
            'ring-0 ring-transparent placeholder:text-blue-100/35 focus:ring-0',
            'min-h-[5.5rem]'
          )}
        />
      </div>
    </section>
  );
}
